/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Emails Hooks - React Query for Email Dashboard
 * ============================================================================
 */

import { useQuery } from '@tanstack/react-query';
import { emailsService, EmailStats, EmailLog, EmailTemplate, EmailStatus, EmailType } from '@/services/superadmin/emails.service';
import { ApiError, PagedResponse } from '@/services/api/types';

export const emailsKeys = {
  all: ['emails'] as const,
  stats: () => [...emailsKeys.all, 'stats'] as const,
  list: (page: number, size: number, filters?: object) => [...emailsKeys.all, 'list', { page, size, ...filters }] as const,
  types: () => [...emailsKeys.all, 'types'] as const,
  templates: () => [...emailsKeys.all, 'templates'] as const,
};

export function useEmailStats() {
  return useQuery<EmailStats, ApiError>({
    queryKey: emailsKeys.stats(),
    queryFn: emailsService.getStats,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useEmails(
  page: number = 0,
  size: number = 20,
  params?: { status?: EmailStatus; type?: EmailType; search?: string }
) {
  return useQuery<PagedResponse<EmailLog>, ApiError>({
    queryKey: emailsKeys.list(page, size, params),
    queryFn: () => emailsService.getEmails(page, size, params),
    staleTime: 30 * 1000,
  });
}

export function useEmailTypes() {
  return useQuery<string[], ApiError>({
    queryKey: emailsKeys.types(),
    queryFn: emailsService.getEmailTypes,
    staleTime: 5 * 60 * 1000,
  });
}

export function useEmailTemplates() {
  return useQuery<EmailTemplate[], ApiError>({
    queryKey: emailsKeys.templates(),
    queryFn: emailsService.getTemplates,
    staleTime: 5 * 60 * 1000,
  });
}
